// MVP transport: owns the sim, pulls human input, runs AI, calls World.step.

import RAPIER from '@dimforge/rapier3d-compat';
import type { Input } from '../core/Input';
import type { InputFrame } from '../core/InputFrame';
import { World } from '../sim/World';
import { AIController } from '../sim/ai/AIController';
import type { AIDifficulty, AIRaycastFn } from '../sim/ai/AIController';
import { MatchState, type TeamMember } from '../sim/MatchState';
import type { SimState } from '../sim/SimState';
import type { NetTransport } from './NetTransport';

const HUMAN_ID = 0;

// Ring capacity for the replay buffer. @60Hz this is ~2s of history —
// comfortably more than the 1s slowmo + 0.5s catchup window.
const BUFFER_LEN = 128;

export interface LocalTransportOptions {
  /** Full 4-member roster (local + teammate + 2 enemies). The first entry
   *  with `isLocal: true` is the human. */
  roster: TeamMember[];
  mapId?: string;
  aiDifficulty?: AIDifficulty;
}

export class LocalTransport implements NetTransport {
  readonly world: World;
  readonly match: MatchState;
  private ais: Map<number, AIController> = new Map();
  private prev: SimState | null = null;
  private latest: SimState | null = null;
  private tickCounter = 0;
  private aiDifficulty: AIDifficulty;
  private buffer: Array<SimState | null> = new Array(BUFFER_LEN).fill(null);
  /** Edge-detect for the sim's per-round `roundOver` flag, so we can fire the
   *  crown increment exactly once when a round ends. */
  private prevRoundOver = false;
  /** While false, tick() steps physics without any AI inputs so the sim
   *  doesn't progress while the renderer is still being built / map assets
   *  are still loading. Mirrors GameRoom.matchStarted on the server. The
   *  caller flips this true via start() once the first render frame has
   *  hit the screen. */
  private started = false;

  constructor(
    private input: Input,
    opts: LocalTransportOptions
  ) {
    this.aiDifficulty = opts.aiDifficulty ?? 'passive';
    this.match = new MatchState(opts.roster);
    const teamById = this.match.teamByIdMap();
    const playerIds = opts.roster.map((m) => m.id);
    this.world = new World({ playerIds, mapId: opts.mapId, teamById });
    const teamOf = (id: number) => this.match.teamOf(id);
    const raycastArena = this.makeArenaRaycaster();
    const getTeammateTargetIds = this.makeTeammateTargetLookup();
    for (const m of opts.roster) {
      if (m.isLocal) continue;
      const ai = new AIController({
        ...AIController.defaults(m.id, this.aiDifficulty),
        teamOf,
        mapId: this.world.arena.mapId,
        raycastArena,
        getTeammateTargetIds
      });
      this.ais.set(m.id, ai);
    }
    // Sanity check the human is id 0 — the rest of the code (camera, fx,
    // debug overlay) still keys on HUMAN_ID = 0 in spots.
    const local = opts.roster.find((m) => m.isLocal);
    if (!local || local.id !== HUMAN_ID) {
      throw new Error('LocalTransport: the local player must have id 0');
    }
  }

  tick(dt: number): void {
    // While a round is over we still step physics (bodies settle, ragdolls
    // keep falling) but we NEVER reset automatically — the UI layer decides
    // when to advance to the next round.
    if (this.input.consumeReset()) {
      // Debug reset = hard reset of current round only.
      this.world.forceReset();
      this.prevRoundOver = false;
    }
    const inputs = new Map<number, InputFrame>();
    const isRoundOver = this.latest?.roundOver ?? false;
    // Freeze inputs once the round ends so the celebration isn't polluted
    // by the human continuing to punch / move.
    if (!isRoundOver && this.started) {
      inputs.set(HUMAN_ID, this.input.poll(this.tickCounter, dt));
      for (const [id, ai] of this.ais) inputs.set(id, ai.produce(this.tickCounter, dt));
    } else {
      // Still poll human input so yaw/pitch accumulators + reset edges stay
      // responsive, but don't feed it into the sim.
      this.input.poll(this.tickCounter, dt);
    }
    const snap = this.world.step(dt, inputs);
    for (const ai of this.ais.values()) ai.observe(snap);

    // Round-end edge: award crown, flip match phase.
    if (snap.roundOver && !this.prevRoundOver) {
      const gameOver = this.match.applyRoundWin(snap.winningTeam);
      void gameOver;
      this.updateFocusForSpectate();
    }
    // Keep spectate focus alive while the round is playing out.
    if (!snap.roundOver && this.match.phase === 'playing') {
      this.updateFocusForSpectate();
    }
    this.prevRoundOver = snap.roundOver;

    this.prev = this.latest;
    this.latest = snap;
    this.buffer[snap.tick % BUFFER_LEN] = snap;
    this.tickCounter += 1;
  }

  /** Release the AI / input freeze so the sim runs at full pace. The
   *  caller is expected to invoke this AFTER the first frame has been
   *  painted, so the user never sees AI mid-action before the screen
   *  has finished its match-entry transition. Idempotent. */
  start(): void {
    this.started = true;
  }

  /** Called by the round-end modal after its 4-second dwell — resets the
   *  round and advances the match-level counters. No-op if the match is
   *  already finished (game-over modal handles that flow). */
  advanceToNextRound(): void {
    if (this.match.phase !== 'roundOver') return;
    this.match.advanceRound();
    this.world.forceReset();
    this.prevRoundOver = false;
    // Reset camera focus to the local player for the new round.
    this.match.focusId = this.match.localPlayer().id;
  }

  /** Snap camera focus to a specific character. Used by spectate cycling. */
  setFocusId(id: number): void {
    if (this.match.memberById(id)) this.match.focusId = id;
  }

  /** Cycle camera focus through still-living characters. Skips any character
   *  that's currently wasted/ringedOut, and skips the local player when
   *  they're dead (since there's no body to follow). */
  cycleFocus(dir: -1 | 1): void {
    const snap = this.latest;
    if (!snap) return;
    const local = this.match.localPlayer();
    const localDead = isDead(snap, local.id);
    const candidates: number[] = [];
    for (const m of this.match.members) {
      if (m.isLocal && localDead) continue;
      if (isDead(snap, m.id)) continue;
      candidates.push(m.id);
    }
    if (candidates.length === 0) return;
    const currIdx = candidates.indexOf(this.match.focusId);
    const n = candidates.length;
    const next = currIdx < 0
      ? candidates[0]!
      : candidates[(currIdx + dir + n) % n]!;
    this.match.focusId = next;
  }

  /** While the round is playing: if the local player dies and their focus
   *  was on themselves, snap to their teammate (fallback: any alive). */
  private updateFocusForSpectate(): void {
    const snap = this.latest;
    if (!snap) return;
    const local = this.match.localPlayer();
    const localDead = isDead(snap, local.id);
    if (!localDead) {
      // If the human is alive, the camera stays on them.
      this.match.focusId = local.id;
      return;
    }
    // Local player is dead — if we're still pinned to them, move focus.
    if (this.match.focusId !== local.id && !isDead(snap, this.match.focusId)) return;
    const teammate = this.match.members.find((m) => !m.isLocal && m.team === local.team);
    if (teammate && !isDead(snap, teammate.id)) {
      this.match.focusId = teammate.id;
      return;
    }
    // Teammate also dead — fall through to any living character.
    const anyAlive = this.match.members.find((m) => !isDead(snap, m.id));
    if (anyAlive) this.match.focusId = anyAlive.id;
  }

  localPlayerIds(): number[] { return [HUMAN_ID]; }
  latestSnapshot(): SimState | null { return this.latest; }
  prevSnapshot(): SimState | null { return this.prev; }
  snapshotAtTick(t: number): SimState | null {
    if (this.latest == null || t < 0 || t > this.latest.tick) return null;
    if (t < this.oldestBufferedTick()) return null;
    return this.buffer[t % BUFFER_LEN];
  }
  oldestBufferedTick(): number {
    if (this.latest == null) return 0;
    return Math.max(0, this.latest.tick - BUFFER_LEN + 1);
  }
  forceReset(): void { this.world.forceReset(); this.prevRoundOver = false; }
  setDifficulty(d: AIDifficulty): void {
    this.aiDifficulty = d;
    const teamOf = (id: number) => this.match.teamOf(id);
    const raycastArena = this.makeArenaRaycaster();
    const getTeammateTargetIds = this.makeTeammateTargetLookup();
    for (const m of this.match.members) {
      if (m.isLocal) continue;
      this.ais.set(m.id, new AIController({
        ...AIController.defaults(m.id, d),
        teamOf,
        mapId: this.world.arena.mapId,
        raycastArena,
        getTeammateTargetIds
      }));
    }
  }

  private makeArenaRaycaster(): AIRaycastFn {
    const rapier = this.world.rapier;
    const ray = new RAPIER.Ray({ x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: 0 });
    return (origin, dir, maxDist) => {
      ray.origin.x = origin.x; ray.origin.y = origin.y; ray.origin.z = origin.z;
      ray.dir.x = dir.x; ray.dir.y = dir.y; ray.dir.z = dir.z;
      const hit = rapier.castRay(
        ray, maxDist, true,
        undefined, undefined, undefined, undefined,
        (c) => {
          const groups = c.collisionGroups();
          const membership = (groups >>> 16) & 0xffff;
          return (membership & ~1) === 0;
        }
      );
      return hit == null ? null : hit.timeOfImpact;
    };
  }

  private makeTeammateTargetLookup(): (myId: number) => Iterable<number> {
    return (myId: number) => {
      const myTeam = this.match.teamOf(myId);
      const ids: number[] = [];
      for (const [id, ai] of this.ais) {
        if (id === myId) continue;
        if (this.match.teamOf(id) !== myTeam) continue;
        const t = ai.currentTargetId;
        if (t != null) ids.push(t);
      }
      return ids;
    };
  }
}

function isDead(snap: SimState, id: number): boolean {
  const c = snap.characters.find((ch) => ch.id === id);
  if (!c) return true;
  return c.wasted || c.ringedOut;
}
